import { useTranslation } from 'next-i18next';
import {
  ArrowUUpLeftIcon,
  SealCheckIcon,
  TagIcon,
  TruckIcon,
  type Icon,
} from '@phosphor-icons/react';
import Tooltip from '_components/UI/Tooltip';

import styles from './PurchaseGuarantee.module.scss';

type PurchaseGuaranteeKey =
  | 'shipping'
  | 'returns'
  | 'satisfaction'
  | 'bestPrice';

interface PurchaseGuaranteeItem {
  key: PurchaseGuaranteeKey;
  icon: Icon;
  title: string;
  tooltip: string;
}

const GUARANTEE_ICON_MAP: Record<PurchaseGuaranteeKey, Icon> = {
  shipping: TruckIcon,
  returns: ArrowUUpLeftIcon,
  satisfaction: SealCheckIcon,
  bestPrice: TagIcon,
};

const PURCHASE_GUARANTEES: PurchaseGuaranteeKey[] = [
  'shipping',
  'returns',
  'satisfaction',
  'bestPrice',
];

const mapGuarantees = (t: (key: string) => string): PurchaseGuaranteeItem[] =>
  PURCHASE_GUARANTEES.map((key) => ({
    key,
    icon: GUARANTEE_ICON_MAP[key],
    title: t(`purchaseGuarantee.${key}.title`),
    tooltip: t(`purchaseGuarantee.${key}.tooltip`),
  }));

const PurchaseGuarantee = () => {
  const { t } = useTranslation('common');
  const guarantees = mapGuarantees(t);

  return (
    <section
      className={styles.purchaseGuarantee}
      aria-label={t('purchaseGuarantee.ariaLabel')}
    >
      <ul className={styles.list}>
        {guarantees.map(({ key, icon: IconComponent, title, tooltip }) => (
          <li key={key} className={styles.listItem}>
            <span className={styles.icon} aria-hidden="true">
              <IconComponent size={20} weight="light" />
            </span>
            <span>
              <span className={styles.title}>{title}</span>
              {tooltip && (
                <span className={styles.tooltipInline}>
                  <Tooltip
                    className={styles.tooltipWrapper}
                    content={tooltip}
                    id={`purchase-guarantee-tooltip-${key}`}
                    iconSize={16}
                  />
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default PurchaseGuarantee;
